/**
 * 自动同步逻辑
 * 
 * 触发时机：
 * 1. 应用启动时
 * 2. 完成一次练习后
 * 3. 应用退出/切到后台时
 */

import { isSyncConfigured, pushToCloud, getDeviceId } from './cloudflareClient';
import { performSync } from './syncManager';
import { extractUserDataPackage } from './dataExtractor';

// 同步间隔：至少 30 秒
const MIN_SYNC_INTERVAL = 30 * 1000;

// 上次同步时间
let lastSyncTime = 0;

// 待同步的变更计数
let pendingChanges = 0;

// 缓存的待发送数据（用于 sendBeacon）
let cachedDataJson: string | null = null;

// 同步 API 地址
const SYNC_API = import.meta.env.VITE_SYNC_API || '/api';

/** 检查是否应该同步 */
function shouldSync(): boolean {
  if (!isSyncConfigured()) return false;
  
  const now = Date.now();
  if (now - lastSyncTime < MIN_SYNC_INTERVAL) {
    console.log('[AutoSync] 跳过：距上次同步不足 30 秒');
    return false;
  }
  
  return true;
}

/** 静默同步（不阻塞 UI） */
async function silentSync(): Promise<void> {
  if (!shouldSync()) return;

  try {
    console.log('[AutoSync] 开始静默同步...');
    const result = await performSync({ silent: true });
    
    if (result.success) {
      lastSyncTime = Date.now();
      pendingChanges = 0;
      console.log('[AutoSync] 同步成功');
    } else {
      console.warn('[AutoSync] 同步失败:', result.error);
    }
  } catch (error) {
    console.warn('[AutoSync] 同步异常:', error);
  }
}

/** 快速上传（只上传，不下载，用于退出时） */
async function quickPush(): Promise<void> {
  if (!isSyncConfigured()) return;
  if (pendingChanges === 0) return;

  try {
    console.log('[AutoSync] 快速上传...');
    const data = await extractUserDataPackage();
    // 缓存数据，供 beforeunload 使用
    cachedDataJson = JSON.stringify(data);
    await pushToCloud(data);
    pendingChanges = 0;
    cachedDataJson = null; // 成功后清除缓存
  } catch (error) {
    console.warn('[AutoSync] 快速上传失败:', error);
    // 保留缓存，让 beforeunload 有机会重试
  }
}

/** 使用 sendBeacon 发送数据（页面卸载时的最后尝试） */
function beaconPush(): void {
  if (!cachedDataJson) return;
  
  const deviceId = getDeviceId();
  const url = `${SYNC_API}/sync?deviceId=${deviceId}`;
  
  try {
    // sendBeacon 限制约 64KB，检查数据大小
    const blob = new Blob([cachedDataJson], { type: 'application/json' });
    if (blob.size > 60 * 1024) {
      console.warn('[AutoSync] 数据过大，sendBeacon 可能失败');
    }
    
    const success = navigator.sendBeacon(url, blob);
    console.log('[AutoSync] sendBeacon:', success ? '已发送' : '失败');
  } catch (error) {
    console.warn('[AutoSync] sendBeacon 异常:', error);
  }
}

/** 标记有待同步的变更 */
export function markPendingChange(): void {
  pendingChanges++;
}

/** 初始化自动同步（应用启动时调用） */
export function initAutoSync(): void {
  if (!isSyncConfigured()) {
    console.log('[AutoSync] 未配置同步服务，跳过自动同步');
    return;
  }

  console.log('[AutoSync] 初始化自动同步');

  // 1. 应用启动时同步
  silentSync();

  // 2. 页面可见性变化时
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // 切到后台/关闭时，快速上传
      quickPush();
    } else if (document.visibilityState === 'visible') {
      // 切回前台时，完整同步
      silentSync();
    }
  });

  // 3. 页面卸载前 - 使用 sendBeacon 进行最后的数据上传
  window.addEventListener('beforeunload', () => {
    if (pendingChanges > 0 || cachedDataJson) {
      beaconPush();
    }
  });
}

/** 练习完成后触发同步 */
export function onPracticeComplete(): void {
  markPendingChange();
  
  // 延迟一点执行，避免阻塞 UI
  setTimeout(() => {
    silentSync();
  }, 1000);
}

/** 手动触发同步（设置页使用） */
export async function triggerManualSync(): Promise<{
  success: boolean;
  message: string;
}> {
  if (!isSyncConfigured()) {
    return { success: false, message: '同步未配置' };
  }

  try {
    const result = await performSync();
    if (result.success) {
      lastSyncTime = Date.now();
      pendingChanges = 0;
      return {
        success: true,
        message: `同步成功：上传 ${result.uploaded ?? 0}，下载 ${result.downloaded ?? 0}`,
      };
    } else {
      return { success: false, message: result.error || '同步失败' };
    }
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : '同步失败',
    };
  }
}
